import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { classNames } from '~/utils/classNames';
import httpClient from '~/lib/http-client';
import { apiGetBillingOverview, type BillingOverview } from '~/lib/api/billing';
import { formatCredits } from '~/lib/plans-model';

interface CreditTransaction {
  id: string;
  type: 'credit' | 'debit';
  amount: number;
  balance: number;
  description: string;
  source: string;
  project?: string;
  createdAt: string;
}

interface GroupedTransactions {
  date: string;
  creditsUsed: number;
  balance: number;
  transactions: CreditTransaction[];
}

interface DailyBucket {
  date: Date;
  label: string;
  used: number;
}

const CARD = 'rounded-xl border border-hairline bg-raised';

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);

  return x;
}

function buildDailyBuckets(groups: GroupedTransactions[]): DailyBucket[] {
  const today = startOfDay(new Date());
  const map = new Map<string, number>();

  for (const g of groups) {
    const d = startOfDay(new Date(g.date));
    map.set(d.toISOString(), (map.get(d.toISOString()) || 0) + g.creditsUsed);
  }

  const buckets: DailyBucket[] = [];

  for (let i = 29; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    buckets.push({
      date: d,
      label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      used: map.get(startOfDay(d).toISOString()) || 0,
    });
  }

  return buckets;
}

export function CreditUsageTab() {
  const { data: overview } = useQuery<BillingOverview>({
    queryKey: ['billing-overview'],
    queryFn: apiGetBillingOverview,
    staleTime: 30_000,
  });
  const [groups, setGroups] = useState<GroupedTransactions[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hoveredBar, setHoveredBar] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    httpClient
      .get<{ transactions: GroupedTransactions[] }>('/api/billing/credit-history', {
        params: { timeRange: 'month', type: 'all', project: 'all' },
      })
      .then(({ data }) => {
        if (!cancelled) {
          setGroups(data.transactions || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setGroups([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const daily = useMemo(() => buildDailyBuckets(groups), [groups]);
  const totalUsed30d = useMemo(() => daily.reduce((s, b) => s + b.used, 0), [daily]);
  const peakUsage = useMemo(() => Math.max(0, ...daily.map((b) => b.used)), [daily]);

  const recent = useMemo(() => {
    const all: CreditTransaction[] = [];

    for (const g of groups) {
      for (const tx of g.transactions) {
        all.push(tx);
      }
    }
    all.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    return all.slice(0, 8);
  }, [groups]);

  const debitCount = useMemo(
    () => groups.reduce((s, g) => s + g.transactions.filter((t) => t.type === 'debit').length, 0),
    [groups],
  );
  const avgPerBuild = debitCount > 0 ? Math.round(totalUsed30d / debitCount) : 0;

  // Touch has no hover and the bars are ~7px wide on a phone — tap or drag across the chart to read a day.
  function selectBarAt(e: React.PointerEvent<HTMLDivElement>) {
    if (e.pointerType === 'mouse' || daily.length === 0) {
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const idx = Math.floor(((e.clientX - rect.left) / rect.width) * daily.length);
    setHoveredBar(Math.min(daily.length - 1, Math.max(0, idx)));
  }

  const creditsAllocated = overview?.creditsAllocated ?? 0;
  const creditsRemaining = overview?.creditsRemaining ?? 0;
  const usedThisCycle = Math.max(0, creditsAllocated - creditsRemaining);
  const percentRemaining = creditsAllocated > 0 ? Math.min(100, (creditsRemaining / creditsAllocated) * 100) : 0;
  const hasTopUp = creditsRemaining > creditsAllocated;

  function renewIn(): string | null {
    if (!overview?.currentPeriodEnd) {
      return null;
    }

    const msPerDay = 1000 * 60 * 60 * 24;
    const diff = new Date(overview.currentPeriodEnd).getTime() - Date.now();
    const days = Math.max(0, Math.round(diff / msPerDay));

    return `${days} ${days === 1 ? 'day' : 'days'}`;
  }

  function formatRelative(dateStr: string): string {
    const ms = Date.now() - new Date(dateStr).getTime();
    const mins = Math.round(ms / 60000);

    if (mins < 1) {
      return 'just now';
    }

    if (mins < 60) {
      return `${mins}m ago`;
    }

    const hrs = Math.round(mins / 60);

    if (hrs < 24) {
      return `${hrs}h ago`;
    }

    const days = Math.round(hrs / 24);

    if (days === 1) {
      return 'yesterday';
    }

    if (days < 7) {
      return `${days}d ago`;
    }

    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  return (
    <div>
      <h3 className="ui-serif m-0 mb-5 text-[24px]">Credit usage</h3>

      {/* Top row: Balance card (1.6fr) + Stats card (1fr) */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1.6fr_1fr]">
        {/* Balance card */}
        <div className="rounded-xl border border-accent/20 bg-raised px-5.5 py-5">
          <div className="ui-caption mb-3 text-accent">Balance</div>

          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="ui-serif text-[32px] leading-none text-primary sm:text-[44px]">
              {formatCredits(creditsRemaining)}
            </span>
            {creditsAllocated > 0 && !hasTopUp && (
              <span className="text-[14px] text-muted">/ {formatCredits(creditsAllocated)}</span>
            )}
          </div>

          <div className="mt-2 text-[12px] leading-normal text-muted">
            {hasTopUp ? (
              <>
                <b className="font-medium text-primary">{formatCredits(creditsAllocated)}</b> monthly
                <span className="mx-1.5 text-faint">·</span>
                <b className="font-medium text-primary">{formatCredits(creditsRemaining - creditsAllocated)}</b> from
                top-up
              </>
            ) : creditsAllocated > 0 ? (
              <>
                <b className="font-medium text-primary">{formatCredits(usedThisCycle)}</b> used this cycle
              </>
            ) : null}
          </div>

          {creditsAllocated > 0 && !hasTopUp && (
            <div className="mt-3.5 h-1 overflow-hidden rounded-full bg-white/5">
              <div
                className="h-full bg-accent transition-[width] duration-300"
                style={{ width: `${percentRemaining}%` }}
              />
            </div>
          )}

          {renewIn() && creditsAllocated > 0 && (
            <div
              className={classNames(
                'flex items-center gap-1.5 border-t border-accent/10 pt-3 text-[11.5px] text-muted',
                hasTopUp ? 'mt-3.5' : 'mt-2.5',
              )}
            >
              <span className="i-ph:arrows-clockwise text-[12px]" />
              Resets in <b className="font-medium text-primary">{renewIn()}</b>
            </div>
          )}
        </div>

        {/* Two stacked stat cards on the right (side by side under the balance on phones) */}
        <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-col">
          <div className={classNames(CARD, 'flex-1 px-4.5 py-3.5')}>
            <div className="ui-caption mb-1.5">Used · 30 days</div>
            <div className="ui-serif text-[22px] leading-none text-primary">
              {Math.round(totalUsed30d).toLocaleString()}
            </div>
            <div className="mt-1 text-[11px] text-muted">credits</div>
          </div>

          <div className={classNames(CARD, 'flex-1 px-4.5 py-3.5')}>
            <div className="ui-caption mb-1.5">Avg / build</div>
            <div className="ui-serif text-[22px] leading-none text-primary">{debitCount > 0 ? avgPerBuild : '—'}</div>
            <div className="mt-1 text-[11px] text-muted">
              {debitCount > 0 ? `across ${debitCount} build${debitCount === 1 ? '' : 's'}` : 'no builds yet'}
            </div>
          </div>
        </div>
      </div>

      {/* Daily usage chart */}
      <div className={classNames(CARD, 'mt-3 px-5.5 pb-4 pt-4.5')}>
        <div className="mb-4 flex flex-wrap items-baseline justify-between gap-x-3">
          <div className="ui-caption">Daily usage · 30 days</div>
          <div className="text-[11.5px] tabular-nums text-muted">
            {hoveredBar !== null && daily[hoveredBar] ? (
              <>
                <span className="font-medium text-primary">{Math.round(daily[hoveredBar].used)}</span> on{' '}
                {daily[hoveredBar].label}
              </>
            ) : peakUsage > 0 ? (
              <>
                Peak <span className="font-medium text-primary">{Math.round(peakUsage)}</span>
              </>
            ) : null}
          </div>
        </div>

        {isLoading ? (
          <div className="flex h-30 items-center justify-center text-[12px] text-muted">
            <span className="i-ph:spinner-gap mr-1.5 animate-spin" />
            Loading…
          </div>
        ) : peakUsage === 0 ? (
          <div className="flex h-30 flex-col items-center justify-center gap-1.5">
            <span className="i-ph:chart-bar text-[20px] text-faint" />
            <span className="text-[12px] text-muted">No usage in the last 30 days</span>
          </div>
        ) : (
          <>
            <div
              className="flex h-25 touch-pan-y items-end gap-[3px]"
              onPointerDown={selectBarAt}
              onPointerMove={selectBarAt}
            >
              {daily.map((b, i) => {
                const h = peakUsage > 0 ? (b.used / peakUsage) * 100 : 0;
                const isHovered = hoveredBar === i;

                return (
                  <div
                    key={i}
                    onPointerEnter={(e) => e.pointerType === 'mouse' && setHoveredBar(i)}
                    onPointerLeave={(e) => e.pointerType === 'mouse' && setHoveredBar(null)}
                    className={classNames(
                      'flex-1 cursor-pointer rounded-sm transition-colors duration-100',
                      b.used === 0 ? 'bg-white/[0.04]' : isHovered ? 'bg-accent' : 'bg-white/[0.16]',
                    )}
                    style={{
                      height: `${Math.max(h, b.used > 0 ? 3 : 2)}%`,
                      minHeight: b.used > 0 ? 3 : 2,
                    }}
                  />
                );
              })}
            </div>
            <div className="mt-2.5 flex justify-between text-[10.5px] text-faint">
              <span>{daily[0]?.label}</span>
              <span>{daily[Math.floor(daily.length / 2)]?.label}</span>
              <span>{daily[daily.length - 1]?.label}</span>
            </div>
          </>
        )}
      </div>

      {/* Recent activity */}
      <div className="ui-caption mb-2.5 mt-6">Recent activity</div>

      {isLoading ? (
        <div className={classNames(CARD, 'px-5 py-6 text-center text-[12.5px] text-muted')}>
          <span className="i-ph:spinner-gap mr-1.5 animate-spin" />
          Loading…
        </div>
      ) : recent.length === 0 ? (
        <div className={classNames(CARD, 'px-5 py-8 text-center')}>
          <span className="i-ph:clock-counter-clockwise text-[20px] text-faint" />
          <div className="mt-1.5 text-[12.5px] text-muted">No recent activity</div>
        </div>
      ) : (
        <div className={classNames(CARD, 'overflow-hidden')}>
          {recent.map((tx, i) => (
            <div
              key={tx.id}
              className={classNames('flex items-center gap-3.5 px-4.5 py-3', i !== 0 && 'border-t border-hairline')}
            >
              <div className="min-w-0 flex-1">
                <div className="overflow-hidden text-ellipsis whitespace-nowrap text-[13px] tracking-[-0.005em] text-primary">
                  {tx.description}
                </div>
                <div className="mt-0.5 text-[11.5px] text-muted">
                  {tx.project && (
                    <>
                      <span className="font-geist-mono text-secondary">{tx.project}</span>
                      <span className="mx-1.5 text-faint">·</span>
                    </>
                  )}
                  {tx.source && (
                    <>
                      {tx.source}
                      <span className="mx-1.5 text-faint">·</span>
                    </>
                  )}
                  {formatRelative(tx.createdAt)}
                </div>
              </div>

              <div
                className={classNames(
                  'flex-shrink-0 text-[13px] font-medium tabular-nums',
                  tx.type === 'credit' ? 'text-accent' : 'text-primary',
                )}
              >
                {tx.type === 'credit' ? '+' : '−'}
                {formatCredits(Math.abs(tx.amount))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
